import { SearchspringSearchProvider } from 'libs/searchspring/fetchers/search'

import clsx from 'clsx'
import { useGridSelector } from 'libs/searchspring/components/collection/useGridSelector'
import { useIntersectionObserver } from '@/components/hooks/useIntersectionObserver'
import { CollectionToolbar } from 'libs/searchspring/components/collection/CollectionToolbar'
import { FloatingToolbarPortal } from 'libs/searchspring/components/collection/FloatingToolbar'
import { useEffect, useState } from 'preact/hooks'
import { FiltersProvider } from 'libs/searchspring/hooks/useFilters'
import { ResultItem } from 'libs/searchspring/components/ResultCardWrapper'
import { useFilterScroll } from 'libs/searchspring/hooks/useFilterScroll'
import { useScrollPositionKeeper } from 'libs/searchspring/hooks/useScrollPositionKeeper'
import { useInfiniteScrollResults } from 'libs/searchspring/hooks/useInfiniteScrollResults'
import { InfiniteScrollNext, InfiniteScrollPrevious } from 'libs/searchspring/components/collection/InfiniteScroll'
import { useOption } from 'libs/searchspring/hooks/useOption'
import { useScrollCache } from 'libs/searchspring/hooks/useScrollCache'
import { SearchHeader } from 'libs/searchspring/components/SearchHeader'
import { scrollHistoryManager } from 'helpers/scroll-history'

const GRID_IMAGE_SIZES = '(min-width: 1828px) 25vw, (min-width: 768px) 33vw, 100vw'
const GRID_IMAGE_WIDTHS = [200, 320, 480, 640, 800, 1000, 1200, 1440]

function SearchGrid() {
  history.scrollRestoration = 'manual'
  const [firstLoad, setFirstLoad] = useState(true)
  const { allResults, gridChange, isLoading, results } = useInfiniteScrollResults()
  const { setEntry } = useScrollPositionKeeper(gridChange)
  const { setScrollElement } = useFilterScroll()
  const query = useOption(
    'query',
    { syncWithURL: true }
  )

  const { corrected = '', original = '' } = results?.query || {}
  const didYouMean = results?.didYouMean?.query || ''

  const corrections = {
    corrected,
    original,
    didYouMean
  }

  // Grid column count toggle
  const { GridSelector, gridTailwind, cssGridVariables, grid } = useGridSelector()

  // Collection Toolbar Visibility
  const { setNode, entry: toolbarRef } = useIntersectionObserver({
    rootMargin: '-100px',
    threshold: 0
  })
  const [toolbarVisible, setToolbarVisible] = useState(true)
  useEffect(() => {
    setToolbarVisible(!toolbarRef?.isIntersecting)
  }, [toolbarRef, toolbarRef?.isIntersecting])

  useEffect(() => {
    if (!isLoading) setFirstLoad(false)
  }, [isLoading])

  useScrollCache([allResults, firstLoad])

  const totalResults = results?.pagination?.totalResults || 0

  return (
    <div className="flex flex-col">
      <SearchHeader query={query} totalResults={totalResults} isLoading={isLoading} corrections={corrections}/>
      <div className="flex flex-col">
        {allResults.length > 0 && !firstLoad && (
          <FiltersProvider >
            <CollectionToolbar GridSelector={GridSelector} setRef={setNode} />
            <FloatingToolbarPortal GridSelector={GridSelector} visible={toolbarVisible} selector={'filter-portal'} />
          </FiltersProvider>
        )}
        {totalResults > 0 ? <div className={'px-3 md:px-6 py-2 md:py-4 text-neutral-secondary paragraph-md'}>{totalResults} Products</div> : null}
        <div
          data-grid-variant={grid.value}
          style={cssGridVariables}
          ref={(el) => { setScrollElement(el); setEntry(el) }}
          className={clsx('grid group/product-grid [grid-auto-flow:row_dense] border-t border-l border-neutral-medium', gridTailwind, (isLoading && firstLoad) && 'min-h-[90vh]')}
        >
          <InfiniteScrollPrevious />
            {
              allResults?.map((item, index) => {
                return <ResultItem item={item} key={item.handle} className="border-b border-r wk-wishlist-button-target border-neutral-medium" dataIndex={index} eager={index === 0} imageSizes={GRID_IMAGE_SIZES} imageWidths={GRID_IMAGE_WIDTHS} />
              })
            }
        </div>
        {allResults.length > 0 && !firstLoad && (
          <div className="flex justify-center px-4 py-6">
            <InfiniteScrollNext />
          </div>
        )}
      </div>
    </div>
  )
}

export function WrappedSearchGrid() {
  scrollHistoryManager.updateHistoryState()
  const urlParams = new URLSearchParams(window.location.search)
  const queryParam = urlParams.get('query')
  return (
    <SearchspringSearchProvider query={queryParam}>
      <SearchGrid />
    </SearchspringSearchProvider>
  )
}
